import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import {
  Box,
  BoxProps,
  Typography,
  styled,
  typographyClasses,
} from "@mui/material";

import {
  decryptExportFile,
  ExportFile,
  isExportFile,
  ExportV2,
} from "@robin/common";

import { PatientImportDialog } from "./PatientImportDialog";

const StyledRoot = styled(Box)(({ theme }) => ({
  alignItems: "center",
  border: `0.75px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(1),
  position: "relative",
  transition: theme.transitions.create("background"),
  "&:hover": {
    background: theme.palette.action.hover,
  },
  [`& .${typographyClasses.root}`]: {
    fontSize: theme.typography.pxToRem(24),
  },
}));

export const FileDropzone: React.FC<
  BoxProps & {
    onFileReceived: (exportFile: ExportFile, data: ExportV2.ExportData) => void;
  }
> = ({ onFileReceived, ...boxProps }) => {
  const { t } = useTranslation();
  const [exportFile, setExportFile] = useState<ExportFile>();
  const [error, setError] = useState<boolean>(false);

  const onDrop = useCallback(async (files: File[]) => {
    //we know already we have one single file
    setExportFile(undefined);
    const file = files[0];
    const buffer = await file.arrayBuffer();
    const textData = new TextDecoder().decode(buffer);
    const data = JSON.parse(textData);
    if (!isExportFile(data)) return;
    setExportFile(data);
  }, []);

  const onPasswordProvided = useCallback(
    (password: string) => {
      if (!exportFile) return;
      try {
        const data = decryptExportFile(exportFile, password);
        onFileReceived(exportFile, data);
        setExportFile(undefined);
        if (error) setError(false);
      } catch (e) {
        console.error(e);
        setError(true);
      }
    },
    [error, exportFile, onFileReceived],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
  });

  return (
    <>
      <StyledRoot
        sx={{
          background: isDragActive ? "background.paper" : "background.light",
        }}
        {...boxProps}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Typography align="center" color="secondary">
          {t("patient.drop-area")}
        </Typography>
      </StyledRoot>
      <PatientImportDialog
        file={exportFile}
        onClose={() => setExportFile(undefined)}
        onComplete={onPasswordProvided}
        onDiscard={() => setExportFile(undefined)}
        error={error}
        open={!!exportFile}
      />
    </>
  );
};

export default FileDropzone;
